"use client";

import type { RefObject } from "react";
import type { EventPageData } from "@/actions/event";
import type { EntityStoryStatus } from "@/actions/story";
import { EventBannerCarousel } from "@/components/events/event-banner-carousel";
import { AvailabilityRibbon } from "@/components/ui/availability-ribbon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MEDIA_SOUND_ABOVE_SHEET_CLASS } from "@/components/ui/media-sound-button";
import { EntityStoryRing } from "@/features/stories/components/entity-story-ring/entity-story-ring.client";
import type { Availability } from "@/lib/availability";
import { firstMediaPreviewUrl } from "@/lib/media";
import { cn } from "@/lib/utils";

interface EventHeroSectionProps {
  heroRef: RefObject<HTMLDivElement | null>;
  eventId: number;
  eventName: string;
  bannerMedia: EventPageData["event"]["bannerMedia"];
  bannerMusic?: EventPageData["event"]["bannerMusic"];
  isReady: boolean;
  storyRingStatus: EntityStoryStatus | null;
  className?: string;
  availability?: Availability;
}

export const EventHeroSection = ({
  heroRef,
  eventId,
  eventName,
  bannerMedia,
  bannerMusic = null,
  isReady,
  storyRingStatus,
  className,
  availability,
}: EventHeroSectionProps) => {
  const heroImageUrl = firstMediaPreviewUrl(bannerMedia);

  return (
    <div
      className={cn(
        "relative overflow-hidden transition-opacity duration-150 md:max-h-[60vh]",
        isReady ? "opacity-100" : "opacity-0",
        className,
      )}
      ref={heroRef}
    >
      <EventBannerCarousel
        bannerMedia={bannerMedia}
        eventName={eventName}
        music={bannerMusic}
        soundButtonClassName={MEDIA_SOUND_ABOVE_SHEET_CLASS}
        // The story ring owns the bottom-left corner when it shows.
        musicCreditClassName={
          storyRingStatus?.hasActive
            ? "bottom-[5.5rem] left-4"
            : MEDIA_SOUND_ABOVE_SHEET_CLASS
        }
      />
      <AvailabilityRibbon availability={availability} className="bottom-16" />
      {storyRingStatus?.hasActive ? (
        <div className="absolute bottom-4 left-4 z-20">
          <EntityStoryRing
            authorType="event"
            authorId={String(eventId)}
            name={eventName}
            avatarUrl={heroImageUrl}
            href={null}
            status={storyRingStatus}
            size={56}
          >
            <Avatar className="h-full w-full rounded-full">
              <AvatarImage src={heroImageUrl ?? undefined} />
              <AvatarFallback>
                {eventName.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </EntityStoryRing>
        </div>
      ) : null}
    </div>
  );
};
